import clsx from 'clsx'
import { EventHandler, HTMLAttributeAnchorTarget, ReactNode, SyntheticEvent, useState } from 'react'
import { BrandLogo } from './BrandLogo'

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'

export function HeaderNav(props: {
  children: ReactNode
  trailingMenu?: ReactNode
  onClickLogo?: () => void
}) {
  return (
    <nav className="flex h-14 flex-row items-stretch justify-between bg-gray-900 px-5 font-sans">
      <div className="flex flex-row">
        <div className="flex items-center">
          <BrandLogo variant="dark" onClick={props.onClickLogo} />
        </div>

        <div className="ml-6 flex items-stretch space-x-6">{props.children}</div>
      </div>
      {props.trailingMenu && <div className="flex flex-row items-center">{props.trailingMenu}</div>}
    </nav>
  )
}

export function HeadNavLink(props: {
  children: ReactNode
  active?: boolean
  href?: string
  target?: HTMLAttributeAnchorTarget
  onClick?: EventHandler<SyntheticEvent<unknown, Event>>
}) {
  return (
    <a
      href={props.href}
      onClick={props.onClick}
      target={props.target}
      className={clsx(
        'flex cursor-pointer items-center border-b-2 text-base',
        props.active && 'border-blue-400 text-blue-400',
        !props.active && 'border-transparent text-white'
      )}
    >
      {props.children}
    </a>
  )
}
export function HeadNavDropdown({ title, children }: { title: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip(), shift({ padding: 8 })]
  })

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon({ buffer: 2 }),
    delay: { open: 40, close: 90 }
  })

  const role = useRole(context, { role: 'menu' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role])

  const linkBase = 'flex cursor-pointer items-center border-b-2 text-base h-full transition-colors'
  const linkColors = open ? 'border-blue-400 text-blue-400' : 'border-transparent text-white'

  return (
    <div className="relative flex items-stretch">
      <a
        ref={refs.setReference}
        {...getReferenceProps({
          'href': '#',
          'onClick': (e: React.MouseEvent) => e.preventDefault(),
          'className': `${linkBase} ${linkColors}`,
          'aria-expanded': open
        })}
      >
        {title}
        <CaretDownIcon />
      </a>

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps({
              className: 'z-50 mt-2 w-62 rounded-xl bg-gray-900/95 p-2 shadow-2xl backdrop-blur-sm'
            })}
          >
            <nav>{children}</nav>
          </div>
        </FloatingPortal>
      )}
    </div>
  )
}

function CaretDownIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden className="ml-1 mt-1 h-4 w-4">
      <path
        fill="currentColor"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.11l3.71-2.88a.75.75 0 1 1 .92 1.18l-4.2 3.25a.75.75 0 0 1-.92 0l-4.2-3.25a.75.75 0 0 1-.02-1.1z"
      />
    </svg>
  )
}

export function HeadNavDropdownLink({
  href,
  children,
  onClick,
  target
}: {
  href?: string
  children: ReactNode
  onClick?: EventHandler<SyntheticEvent<unknown, Event>>
  target?: HTMLAttributeAnchorTarget
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      target={target}
      className="block cursor-pointer rounded-lg px-3 py-2 text-left text-base text-white hover:bg-white/5 focus:bg-white/5 focus:outline-none"
    >
      {children}
    </a>
  )
}
